import React, { useEffect, useState, useMemo } from 'react';
import * as Sentry from '@sentry/browser';
import { listAllLibraryTopicTemplates } from '../../graphql/resourceLibraryQueries';
import { LibraryTopicTemplate, LibraryCategoryTopicRef } from '../../types/resourceLibraryTypes';
import { CustomModal } from '../CustomModal';
import { Btn, IconSearch, TabNav, TEIQspinner } from '../ui';
import '../../css/ResourceLibraryContent.css';

interface TopicPoolPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    categoryName: string;
    alreadyAddedTopicIds: string[];
    onAddTopics: (topics: LibraryCategoryTopicRef[]) => void;
}

const STYLE_FILTERS = ['All', 'Video', 'PDF', 'Poster', 'FatalGram'];

export const TopicPoolPickerModal: React.FC<TopicPoolPickerModalProps> = ({
    isOpen,
    onClose,
    categoryName,
    alreadyAddedTopicIds,
    onAddTopics,
}) => {
    const [topics, setTopics] = useState<LibraryTopicTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [styleFilter, setStyleFilter] = useState('All');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Fetch topics on open
    useEffect(() => {
        if (!isOpen) return;
        const fetchTopics = async () => {
            setLoading(true);
            try {
                const all = (await listAllLibraryTopicTemplates()) as LibraryTopicTemplate[];
                setTopics(all);
            } catch (err) {
                console.error('Error fetching topic templates:', err);
                Sentry.captureException(err);
            } finally {
                setLoading(false);
            }
        };
        fetchTopics();
        setSelectedIds(new Set());
        setSearchQuery('');
        setStyleFilter('All');
    }, [isOpen]);

    const alreadyAddedSet = useMemo(() => new Set(alreadyAddedTopicIds), [alreadyAddedTopicIds]);

    // Filter topics by search + style
    const filteredTopics = useMemo(() => {
        let result = topics;
        if (styleFilter !== 'All') {
            result = result.filter((t) => t.style === styleFilter);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(
                (t) =>
                    t.topicId.toLowerCase().includes(q) ||
                    t.description.toLowerCase().includes(q)
            );
        }
        return result;
    }, [topics, styleFilter, searchQuery]);

    const handleToggle = (topicId: string) => {
        if (alreadyAddedSet.has(topicId)) return;
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(topicId)) next.delete(topicId);
            else next.add(topicId);
            return next;
        });
    };

    const handleAdd = () => {
        const selected = topics.filter((t) => selectedIds.has(t.topicId));
        const refs: LibraryCategoryTopicRef[] = selected.map((t, i) => ({
            libraryTopicTemplateId: t.id,
            topicId: t.topicId,
            description: t.description,
            style: t.style,
            timeValue: t.timeValue,
            sortOrder: i + 1, // will be re-assigned by parent
        }));
        onAddTopics(refs);
    };

    const selectedCount = selectedIds.size;

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={onClose}
            size="large"
            header={`Add Topics — ${categoryName}`}
            bodyClassName="zrl-dialog-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={`Add ${selectedCount} ${selectedCount === 1 ? 'Topic' : 'Topics'} to ${categoryName}`}
                        buttonDisabled={selectedCount === 0}
                        buttonOnClick={handleAdd}
                    />
                </div>
            }
        >
                    <p className="zrl-modal-subtitle">
                        Select from the Zorva topic pool. Topics marked &quot;added&quot; are already in this category.
                    </p>
                    <div className="zrl-search-bar">
                        <span className="zrl-search-icon"><IconSearch /></span>
                        <input
                            type="text"
                            placeholder="Search topics..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {/* Style Filter Tabs */}
                    <TabNav
                        variant="pills"
                        className="tab-nav-stand-alone"
                        ariaLabel="Topic style"
                        tabs={STYLE_FILTERS.map((sf) => ({ id: sf, label: sf }))}
                        activeTabId={styleFilter}
                        onTabChange={setStyleFilter}
                    />

                    {/* Topic List */}
                    {loading ? (
                        <div className="zrl-loading">
                            <TEIQspinner label="Loading topics" />
                        </div>
                    ) : filteredTopics.length === 0 ? (
                        <div className="zrl-empty">
                            {topics.length === 0
                                ? 'No topics in the Zorva pool yet. Create topics by saving plans from the Safety & Training module.'
                                : 'No topics match your search.'}
                        </div>
                    ) : (
                        <div className="zrl-company-list">
                            {filteredTopics.map((topic) => {
                                const isAdded = alreadyAddedSet.has(topic.topicId);
                                const isSelected = selectedIds.has(topic.topicId);
                                return (
                                    <div
                                        key={topic.id}
                                        className={`zrl-company-row ${isSelected ? 'checked' : ''}`}
                                        style={{ opacity: isAdded ? 0.6 : 1 }}
                                        onClick={() => handleToggle(topic.topicId)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected || isAdded}
                                            disabled={isAdded}
                                            onChange={() => handleToggle(topic.topicId)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className="zrl-company-info">
                                            <div className="zrl-company-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--slate-gray)' }}>
                                                    {topic.topicId}
                                                </span>
                                                <span>{topic.description}</span>
                                            </div>
                                        </div>
                                        <span className="zrl-badge zrl-badge-style" style={{
                                            background: topic.style === 'Video' ? '#e8edf8' :
                                                topic.style === 'PDF' ? '#fde8e8' :
                                                    topic.style === 'Poster' ? '#fef3cd' : '#e8f8ef',
                                            color: topic.style === 'Video' ? '#2c5282' :
                                                topic.style === 'PDF' ? '#c0392b' :
                                                    topic.style === 'Poster' ? '#856404' : '#1a8a4a',
                                        }}>
                                            {topic.style}
                                        </span>
                                        <span style={{ fontSize: '0.78rem', color: 'var(--slate-gray)', whiteSpace: 'nowrap' }}>
                                            {topic.timeValue} min
                                        </span>
                                        {isAdded && (
                                            <span className="zrl-badge zrl-badge-active" style={{ marginLeft: 4 }}>Added</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Selected Count */}
                    {selectedCount > 0 && (
                        <div className="zrl-assign-count">
                            <strong>{selectedCount}</strong> {selectedCount === 1 ? 'topic' : 'topics'} selected to add to {categoryName}
                        </div>
                    )}
        </CustomModal>
    );
};
